import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Accordion, Tooltip } from '@cimpress/react-components';
import { getAllDocuments } from 'src/selectors/layers';
import { toSelectOption } from 'src/util/misc';
import EachProductVariable from './EachProductVariable';
import { updateConfiguration, removeConfiguration, addConfiguration } from './slice';
import getSceneVariation from './selectors/getSceneVariation';
import ErrorIcon from 'src/components/icons/ErrorIcon';

const PRODUCT_CONFIG_KEY = 'productConfigurations';
const ERROR_MSG = 'One or more product variable configurations have errors. Please fix them.';

const ProductVariables = (props) => {
    const hasErrors = useMemo(
        () => props.productConfigurations.some((each) => !!each.errorMessage),
        [props.productConfigurations],
    );

    const addNewProductConfiguration = (event) => {
        event.stopPropagation();
        props.addConfiguration({ key: PRODUCT_CONFIG_KEY });
    };
    const title = (
        <div data-testid='scene-variation-product-variables-title' className='layers-header'>
            <div className='layers-header__text'>
                {hasErrors && (
                    <Tooltip direction='top' contents={ERROR_MSG}>
                        <ErrorIcon />
                    </Tooltip>
                )}
                Product Variables
            </div>
            <button onClick={addNewProductConfiguration}>
                <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/add-2-l.svg' />
            </button>
        </div>
    );
    const { productConfigurations } = props;

    // A document can only have one configuration per (variableOption, variableAttribute) pair —
    // e.g. two "Blending Mode" configs for the same document+attribute would be a genuine
    // conflict. Different variableOptions (blending/finishes/colorProfile) don't conflict with
    // each other on the same document — blending is stored under a separate `{document}-wrapper`
    // key, and finishes/colorProfile are appended as independent entries — so only documents
    // already used by *this same* variableOption+attribute combination should be excluded.
    const getDocumentOptions = (variableAttributeValue, variableOptionValue) => {
        let documentsUsed = productConfigurations.filter(
            ({ variableOption, variableAttribute }) =>
                variableOption === variableOptionValue && variableAttribute === variableAttributeValue,
        );
        documentsUsed = documentsUsed.filter((each) => !!each.document).map((each) => each.document);
        return toSelectOption(props.documents.map((each) => each.id).filter((each) => !documentsUsed.includes(each)));
    };

    // Check if the selections are still a valid option and return error message if any is not valid
    const getErrorMessage = useCallback(
        (configuration) => {
            const { variableAttribute, layer, document } = configuration;

            if (variableAttribute && !props.attributesOptions.some((attr) => attr.name === variableAttribute)) {
                return 'Selected attribute is no longer a valid option or is in conflict with attribute(s) in link information section. Please remove the product variable configuration or remove the conflicting attributes form the link information section!';
            }

            if (layer && !props.assetList.some((doc) => doc.name === layer.label)) {
                return 'Selected image layer is no longer a valid option. Please remove and redo the product variable configuration with a valid image layer.';
            }

            if (document && !props.documents.some((doc) => doc.id === document)) {
                return 'Selected document is no longer a valid option. Please remove and redo the product variable configuration with a valid document.';
            }

            return '';
        },
        [props.attributesOptions, props.assetList, props.documents],
    );

    useEffect(() => {
        props.productConfigurations.forEach((config, index) => {
            const errorMessage = getErrorMessage(config);
            if (errorMessage !== config.errorMessage) {
                props.updateConfiguration({
                    key: PRODUCT_CONFIG_KEY,
                    index,
                    newConfiguration: { ...config, errorMessage },
                });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.documents, props.assetList, props.attributesOptions]);

    return (
        <Accordion title={title} defaultOpen={false}>
            <div className='accordion-body-wrapper'>
                {productConfigurations.map((each, index) => (
                    <EachProductVariable
                        /* eslint-disable react/no-array-index-key */
                        key={index}
                        index={index}
                        configuration={each}
                        updateConfiguration={(data) =>
                            props.updateConfiguration({ key: PRODUCT_CONFIG_KEY, index, newConfiguration: data })
                        }
                        removeConfiguration={() => props.removeConfiguration({ key: PRODUCT_CONFIG_KEY, index })}
                        attributesOptions={props.attributesOptions}
                        assetOptions={props.assetOptions}
                        getDocumentOptions={getDocumentOptions}
                    />
                ))}
            </div>
        </Accordion>
    );
};

const optionShape = PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
});

ProductVariables.propTypes = {
    attributesOptions: PropTypes.arrayOf(PropTypes.object),
    productConfigurations: PropTypes.arrayOf(
        PropTypes.shape({
            layer: optionShape,
            variableAttribute: PropTypes.string,
            variableOption: PropTypes.string,
            values: PropTypes.object,
        }),
    ),
    updateConfiguration: PropTypes.func.isRequired,
    removeConfiguration: PropTypes.func.isRequired,
    addConfiguration: PropTypes.func.isRequired,
    documents: PropTypes.arrayOf(PropTypes.any),
    assetOptions: PropTypes.arrayOf(optionShape),
    assetList: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        }),
    ),
};

function mapStateToProps(state) {
    const { productConfigurations } = getSceneVariation(state);
    const documents = getAllDocuments(state);

    return {
        productConfigurations,
        documents,
    };
}

export default connect(mapStateToProps, {
    updateConfiguration,
    removeConfiguration,
    addConfiguration,
})(ProductVariables);
